import { css, DefaultTheme } from 'styled-components';
import { Theme as DeepDiveTheme, theme as deepDiveTheme } from '@algoan/deep-dive/theme';
import { merge } from 'lodash';
import { ThemedCss } from '@algoan/utils/theme';

/**
 * Colors for the new Dashboard
 */
interface DashboardTheme {
  /**
   * Font family for the project
   */
  fontFamily: string;

  main: {
    /**
     * Active element
     */
    deepBlue: string;
  };
  secondary: {
    /**
     * Inactive input
     */
    softBlue1: string;
    /**
     * Secondary font
     */
    softBlue2: string;
    /**
     * Active input
     */
    softBlue3: string;
    /**
     * Inactive menu
     */
    softBlue4: string;
    /**
     * blue for the background
     */
    softBlue5: string;
    /**
     * Chart main axis
     */
    skyBlue: string;
    /**
     * Labels
     */
    chalk: string;
  };
  /**
   * Colors used for teams, accounts, graphics, etc
   */
  extraColors: {
    apricot: string;
    blueLagoon: string;
    sweetPink: string;
    mauve: string;
    lemonJuice: string;
    fushia: string;
    chocolate: string;
    summerBlue: string;
    sunFlower: string;
    kakiGreen: string;
    sweetGrey: string;
    deepGrey: string;
    bluePeps: string;
    silver: string;
    orangeWarning: string;
    warning: string;
    softOrange: string;
    greenIndicator: string;
  };
  /**
   * Legacy theme color values for old Dashboard
   */
  legacy: {
    secondary: string;
    danger: string;
    veryLightGrey: string;
    lighterGrey: string;
    lightGrey: string;
    green: string;
    red: string;
    blue: string;
  };
  /**
   * Colors used for the skeleton loaders
   */
  loadingGradient: {
    primary: {
      mainColor: string;
      shadowColor: string;
    };
    secondary: {
      mainColor: string;
      shadowColor: string;
    };
  };
  /**
   * Colors used for the analysis state details
   * In case the analysis is not completed
   */
  errorStateColors: {
    grey: string;
    orange: string;
  };
  media: {
    portrait: (...args: Parameters<ThemedCss<DefaultTheme>>) => ReturnType<ThemedCss<DefaultTheme>>;
  };
}

export const dashboardTheme: DashboardTheme = {
  fontFamily: `'Muli', sans-serif`,

  main: {
    deepBlue: '#2B86D1',
  },
  secondary: {
    softBlue1: '#A2B2CD',
    softBlue2: '#6B8DB5',
    softBlue3: '#3E729D',
    softBlue4: '#316995',
    softBlue5: '#EAF4FC',
    skyBlue: '#D0D8E5',
    chalk: '#F8FAFB',
  },

  extraColors: {
    apricot: '#F6A46A',
    blueLagoon: '#81E4DF',
    sweetPink: '#FF597A',
    mauve: '#8C81FF',
    lemonJuice: '#E9E260',
    fushia: '#EC62FF',
    chocolate: '#986A62',
    summerBlue: '#53AEFF',
    sunFlower: '#FFCC45',
    kakiGreen: '#849C71',
    sweetGrey: '#ABB3C0',
    deepGrey: '#798E9F',
    bluePeps: '#0B7ACE',
    silver: '#B9C6CF',
    orangeWarning: '#FFB126',
    warning: '#F46F0E',
    softOrange: '#FFEEE1',
    greenIndicator: '#12B17A',
  },

  legacy: {
    secondary: '#7fa2b5',
    danger: '#f44336',
    veryLightGrey: '#f3f7f9',
    lighterGrey: '#ecf1f4',
    lightGrey: '#d8e2e7',
    green: '#2acf4a',
    red: '#ff6348',
    blue: '#48a2f4',
  },
  loadingGradient: {
    primary: {
      mainColor: '#e0e8ec',
      shadowColor: '#ebf0f3',
    },
    secondary: {
      mainColor: '#ccdae2',
      shadowColor: '#e0e9ed',
    },
  },
  errorStateColors: {
    grey: '#CDDBE2',
    orange: '#FF9A52',
  },
  media: {
    portrait: (...args: Parameters<ThemedCss<DefaultTheme>>): ReturnType<ThemedCss<DefaultTheme>> => css`
      @media only screen and (max-width: ${1200 / 16}em) {
        ${css(...args)}
      }
    `,
  },
};

export const theme = merge(deepDiveTheme, dashboardTheme);

type Theme = DeepDiveTheme & DashboardTheme;

declare module 'styled-components' {
  // eslint-disable-next-line
  interface DefaultTheme extends Theme {}
}

export const accountColors = [
  theme.extraColors.apricot,
  theme.extraColors.blueLagoon,
  theme.extraColors.sweetPink,
  theme.extraColors.mauve,
  theme.extraColors.lemonJuice,
  theme.extraColors.fushia,
  theme.extraColors.chocolate,
  theme.extraColors.summerBlue,
  theme.extraColors.sunFlower,
  theme.extraColors.kakiGreen,
];
